import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper, { Navigation } from 'swiper';

import { aosRefresh } from '../../../../../global/js/core/core';
import SplitText from '../../../../../global/js/vendors/gsap-splittext/splittext';

/**
 * Animates elements inside the hero section.
 * Registers GSAP plugins, initializes animations, and handles responsive behavior.
 *
 * @function hero10
 */
function hero10() {
    Swiper.use([Navigation]);

    const slider = document.querySelector('.js-o-hero-10__press-wrapper-box');
    if (slider) {
        new Swiper(slider, {
            loop: true,
            speed: 600,
            navigation: {
                nextEl: slider.querySelector('.swiper-button-next'),
                prevEl: slider.querySelector('.swiper-button-prev'),
            },
        });
    }

    // Disabling browser's scrollRestoration feature to ensure user always sees the very top of a page.
    if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
    }
    // Shows the very top of a page.
    window.scrollTo({ top: 0, behavior: 'instant' });

    // Register GSAP plugins
    gsap.registerPlugin(ScrollTrigger, SplitText);

    /**
     * Retrieves the hero type from a data attribute.
     * Ensures the attribute is present to avoid errors.
     *
     * @type {string | null}
     */
    const heroTypeElement = document.querySelector('[data-hero-type]');
    const heroType = heroTypeElement ? heroTypeElement.dataset.heroType : null;

    if (!heroType) {
        return;
    }

    // Element selectors
    const pretitle = document.querySelector(`.o-hero-${heroType}__pretitle`);
    const title = document.querySelector(`.o-hero-${heroType} h1.a-title`);
    const subtitleContent = document.querySelector(
        `.o-hero-${heroType}__row--1 .o-hero-${heroType}__content-wrapper .a-text`,
    );
    const regularButtonWrapper = document.querySelector(
        `.o-hero-${heroType}__buttons-repeater`,
    );

    /**
     * Timeline instance for hero animations.
     *
     * @type {gsap.core.Timeline}
     */
    const tl = gsap.timeline();

    // Content animations
    // Animate pretitle if exists
    if (pretitle) {
        tl.fromTo(
            pretitle,
            { opacity: 0, yPercent: -100 },
            { duration: 1, opacity: 1, yPercent: 0, ease: 'power4.inOut' },
            '<',
        );
    }

    // Animate title if exists
    if (title) {
        tl.fromTo(
            title,
            { autoAlpha: 0 },
            { duration: 0.3, autoAlpha: 1, ease: 'power4.inOut' },
            '<',
        );

        /**
         * Splits title text into characters for animation.
         *
         * @type {SplitText}
         */
        const split = new SplitText(title, {
            tag: 'span',
            type: 'chars,words',
            wordsClass: 'overflow-hidden',
        });

        tl.from(
            split.chars,
            {
                duration: 1.2,
                yPercent: 100,
                ease: 'power4.inOut',
                stagger: { amount: 0.4 },
            },
            '<',
        );
    }

    // Animate subtitle if exists
    if (subtitleContent) {
        tl.fromTo(
            subtitleContent,
            { opacity: 0 },
            { duration: 0.5, opacity: 1, ease: 'power4.inOut' },
            '<+=1',
        );
    }

    // Animate buttons if exists
    if (regularButtonWrapper) {
        tl.fromTo(regularButtonWrapper, { opacity: 0 }, { opacity: 1 }, '<');
    }

    // Sidebar animation functionality
    /**
     * Responsive animation adjustments for screen widths ≥ 1024px.
     */
    if (gsap && typeof gsap.matchMedia === 'function') {
        const mm = gsap.matchMedia();

        mm.add('(min-width: 1024px)', () => {
            /**
             * Updates the sidebar animation on resize.
             * Ensures smooth scrolling effect and proper positioning.
             */
            const updateAnimation = () => {
                ScrollTrigger.getAll().forEach((trigger) => trigger.kill());

                const sidebar = document.querySelector('.o-hero-10__sidebar');
                const header = document.querySelector('.o-header-1');
                const heroContainer = document.querySelector(
                    '.o-hero-10__container',
                );
                if (!sidebar || !header) {
                    return;
                }

                const heroContainerHeight =
                    heroContainer.getBoundingClientRect().height;
                const sidebarHeight = sidebar.getBoundingClientRect().height;
                const offset = (heroContainerHeight - sidebarHeight) / 2;

                gsap.to(sidebar, {
                    y: () => {
                        const sidebarTop =
                            sidebar.getBoundingClientRect().top +
                            window.scrollY;
                        const headerBottom =
                            header.getBoundingClientRect().bottom +
                            window.scrollY;
                        return -(sidebarTop - headerBottom - offset);
                    },
                    ease: 'none',
                    scrollTrigger: {
                        start: 'top top',
                        end: () => {
                            const sidebarTop =
                                sidebar.getBoundingClientRect().top +
                                window.scrollY;
                            const headerBottom =
                                header.getBoundingClientRect().bottom +
                                window.scrollY;
                            return `+=${sidebarTop - headerBottom - offset}`;
                        },
                        scrub: true,
                        pin: '.o-hero-10',
                        once: true,
                        onLeave: function (self) {
                            let start = self.start;
                            self.scroll(start);
                            self.disable();
                            self.animation.progress(1, true);
                            ScrollTrigger.refresh();
                            window.scrollTo(0, start);
                            aosRefresh();
                        },
                        markers: false,
                    },
                });

                // Refresh ScrollTrigger after animations are updated
                ScrollTrigger.refresh();
                aosRefresh();
            };

            updateAnimation();

            // Handle resizing
            window.addEventListener('resize', updateAnimation);

            return () => {
                window.removeEventListener('resize', updateAnimation);
                ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
            };
        });
    }

    /**
     * Enables smooth scrolling for anchor navigation.
     */
    document.addEventListener('DOMContentLoaded', () => {
        const navLinks = document.querySelectorAll(
            '.o-hero-10__anchor-navigation-list-item-link',
        );

        navLinks.forEach((link) => {
            link.addEventListener('click', (event) => {
                const targetId = link.getAttribute('href');

                if (targetId.startsWith('#')) {
                    const targetElement = document.querySelector(targetId);

                    if (targetElement) {
                        event.preventDefault();

                        // Smooth scroll behavior
                        window.scrollTo({
                            top: targetElement.offsetTop,
                            behavior: 'smooth',
                        });
                    }
                }
            });
        });
    });
}

export default hero10;
